import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import AdsAPIs from "../../apiRoutes/Ads";
import AgencyAPIs from "../../apiRoutes/Agency";
import { GET_RATE_CARDS_QUERY_KEY, GET_AGENCIES_QUERY_KEY, GET_AD_ADTYPES_QUERY_KEY } from "../../utils/constants/queryKeys";
import SectionHeader from "../common/SectionHeader";
import ButtonLoader from "../Icons/ButtonLoader";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import RateCardModal from "./Modals/RateCardModal";
import AddAdTypeAndRateCardModal from "./Modals/AddAdTypeAndRateCardModal";
import HookPricingModal from "./Modals/HookPricingModal";
import { DatePicker } from "../ui/date-picker";
import { format, startOfMonth, endOfMonth } from "date-fns";
import toast from "react-hot-toast";
import { Download, Loader2, Pencil } from "lucide-react";
import { Tooltip } from "@mui/material";
import {
  fetchAllFilteredRows,
  downloadExcelFromRows,
  exportFilename,
} from "../../utils/exportFilteredListToExcel";
import usePermissions from "../../hooks/usePermissions";
import { PERMISSION_CODES } from "../../utils/constants/permissions";
import useZustandStore from "../../zustandStore/useZustandStore";
import { agencyDisplayName, getAgencyIdFromUser, isAgencyScopedUser } from "../../utils/agencyScope";

const HOOK_SUMMARY_QUERY_KEY = ["rateCardHookSummary"];

const LIMIT = 20;

const AdRateCardSection = () => {
  const [modal, setModal] = useState({ open: false, editData: null });
  const [addCombinedOpen, setAddCombinedOpen] = useState(false);
  const [hookPricingModal, setHookPricingModal] = useState({ open: false, agency: null });
  const [hookDateFrom, setHookDateFrom] = useState(startOfMonth(new Date()));
  const [hookDateTo, setHookDateTo] = useState(endOfMonth(new Date()));
  const [page, setPage] = useState(0);
  const [agencyFilter, setAgencyFilter] = useState("");
  const [adTypeFilter, setAdTypeFilter] = useState("");
  const [exportingExcel, setExportingExcel] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const currentUser = useZustandStore((s) => s?.currentUser);
  const scopedAgencyId = getAgencyIdFromUser(currentUser);
  const agencyScoped = isAgencyScopedUser(currentUser);
  const { hasPermission } = usePermissions();
  const canViewRateCard =
    hasPermission(PERMISSION_CODES.RATE_CARD_VIEW) || hasPermission(PERMISSION_CODES.RATE_CARD_MANAGE);
  const canManageRateCard = hasPermission(PERMISSION_CODES.RATE_CARD_MANAGE);
  /** Listing agencies requires agency.manage; avoids 403 + toast for rate-card-only roles. */
  const canListAgencies = hasPermission(PERMISSION_CODES.AGENCY_MANAGE);

  const buildListParams = (limit, offset) => ({
    axiosPrivate,
    agency_id: agencyScoped && scopedAgencyId != null ? scopedAgencyId : agencyFilter ? Number(agencyFilter) : undefined,
    adtype_id: adTypeFilter ? Number(adTypeFilter) : undefined,
    limit,
    offset,
  });

  const { data: result, isLoading } = useQuery({
    queryKey: [...GET_RATE_CARDS_QUERY_KEY, page, agencyFilter, agencyScoped, scopedAgencyId, adTypeFilter],
    queryFn: () => AdsAPIs.listRateCards(buildListParams(LIMIT, page * LIMIT)),
  });

  const { data: agenciesResult } = useQuery({
    queryKey: GET_AGENCIES_QUERY_KEY,
    queryFn: () => AgencyAPIs.list({ axiosPrivate, limit: 200, offset: 0 }),
    enabled: !agencyScoped && canListAgencies,
  });
  const agencies = agenciesResult?.data ?? [];

  const { data: adtypesResult } = useQuery({
    queryKey: GET_AD_ADTYPES_QUERY_KEY,
    queryFn: () => AdsAPIs.listAdtypes({ axiosPrivate, limit: 200, offset: 0 }),
  });
  const adtypes = adtypesResult?.data ?? [];

  const hookDateFromStr = hookDateFrom ? format(hookDateFrom, "yyyy-MM-dd") : undefined;
  const hookDateToStr = hookDateTo ? format(hookDateTo, "yyyy-MM-dd") : undefined;

  const { data: hookSummaryResult, isLoading: hookSummaryLoading } = useQuery({
    queryKey: [...HOOK_SUMMARY_QUERY_KEY, agencyScoped, scopedAgencyId, hookDateFromStr, hookDateToStr],
    queryFn: () => AdsAPIs.getHookSummary({
      axiosPrivate,
      agency_id: agencyScoped && scopedAgencyId != null ? scopedAgencyId : undefined,
      date_from: hookDateFromStr,
      date_to: hookDateToStr,
    }),
  });
  const hookSummaryRows = hookSummaryResult?.data ?? [];

  const rateCards = result?.data ?? [];
  const totalCount = result?.count ?? 0;

  const { mutate: deleteCard, isPending: isDeleting } = useMutation({
    mutationFn: (id) => AdsAPIs.deleteRateCard({ axiosPrivate, id }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: GET_RATE_CARDS_QUERY_KEY });
      toast.success("Rate card deleted.");
    },
    onError: (e) => toast.error(e?.response?.data?.error_message || e?.message),
  });

  useEffect(() => setPage(0), [agencyFilter, adTypeFilter]);

  const handleExportExcel = async () => {
    if (exportingExcel) return;
    setExportingExcel(true);
    toast.loading("Preparing export…", { id: "export-excel-rate" });
    try {
      const { rows, total } = await fetchAllFilteredRows({
        fetchPage: ({ limit, offset }) => AdsAPIs.listRateCards(buildListParams(limit, offset)),
      });
      if (!rows.length) {
        toast.dismiss("export-excel-rate");
        toast.error("No rows to export.");
        return;
      }
      await downloadExcelFromRows({
        filename: exportFilename("ad-rate-cards"),
        sheetName: "Rate cards",
        columns: [
          { header: "ID", value: (r) => r.id },
          { header: "Ad type", value: (r) => r.adtype_name ?? "" },
          { header: "Price per unit", value: (r) => r.price_per_unit ?? "" },
          { header: "Currency", value: (r) => r.currency ?? "" },
        ],
        rows,
      });
      toast.dismiss("export-excel-rate");
      toast.success(`Exported ${total} row(s).`);
    } catch (e) {
      toast.dismiss("export-excel-rate");
      if (e?.message === "EXPORT_TOO_LARGE") {
        toast.error(`Too many rows (${e.total}). Max export is ${e.maxRows}. Narrow filters.`);
      } else {
        toast.error(e?.message || "Export failed.");
      }
    } finally {
      setExportingExcel(false);
    }
  };

  const handleDelete = (row) => {
    if (!window.confirm(`Delete this rate card (${row.adtype_name || "ad type"})?`)) return;
    deleteCard(row.id);
  };

  if (isLoading) {
    return (
      <section className="p-4 md:p-6">
        <SectionHeader title="Ad Rate Card" className="mb-4" />
        <div className="flex items-center justify-center min-h-[320px] gap-2">
          <ButtonLoader color="#7f7ad5" />
          <span className="text-gray-600 font-medium">Loading rate cards…</span>
        </div>
      </section>
    );
  }

  return (
    <section className="p-4 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <SectionHeader title="Ad Rate Card" />
        <div className="flex items-center gap-2">
          {canViewRateCard && (
            <Tooltip title="Download Excel">
              <span>
                <Button
                  type="button"
                  variant="outline"
                  className="h-9 w-9 p-0 shrink-0"
                  aria-label="Download Excel"
                  disabled={exportingExcel || isLoading}
                  onClick={handleExportExcel}
                >
                  {exportingExcel ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </Button>
              </span>
            </Tooltip>
          )}
          {canManageRateCard && (
            <Button onClick={() => setAddCombinedOpen(true)}>
              Add
            </Button>
          )}
        </div>
      </div>

      {/* Hook Pricing - simple card for agency-scoped users */}
      {agencyScoped ? (
        <div className="rounded-xl border border-[#e5e7eb] bg-white p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-sm font-semibold text-gray-700">Your Hook Pricing</span>
            {hookSummaryLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          </div>
          <div className="grid grid-cols-2 gap-6 max-w-md">
            <div>
              <p className="text-xs text-gray-500 mb-1">Max free hooks</p>
              <p className="text-lg font-semibold text-gray-800">{hookSummaryRows[0]?.max_free_hooks ?? "—"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Cost per hook</p>
              <p className="text-lg font-semibold text-gray-800">{hookSummaryRows[0]?.cost_per_hook ?? "—"}</p>
            </div>
          </div>
        </div>
      ) : (
      /* Hook Pricing Summary */
      <div className="rounded-xl border border-[#e5e7eb] bg-white overflow-hidden mb-6">
        <div className="p-4 flex flex-wrap gap-3 items-center border-b border-gray-200">
          <span className="text-sm font-semibold text-gray-700 mr-2">Hook Pricing Summary</span>
          <DatePicker value={hookDateFrom} onChange={setHookDateFrom} placeholder="Approved from" className="h-9 w-[200px] shrink-0" />
          <DatePicker value={hookDateTo} onChange={setHookDateTo} placeholder="Approved to" className="h-9 w-[200px] shrink-0" />
          {(hookDateFrom || hookDateTo) && (
            <Button type="button" variant="outline" size="sm" className="h-9" onClick={() => { setHookDateFrom(null); setHookDateTo(null); }}>
              All time
            </Button>
          )}
          {hookSummaryLoading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                {!agencyScoped && <th className="py-3 px-4 text-sm font-semibold text-gray-600">Agency</th>}
                <th className="py-3 px-4 text-sm font-semibold text-gray-600">Max free hooks</th>
                <th className="py-3 px-4 text-sm font-semibold text-gray-600">Cost / hook</th>
                <th className="py-3 px-4 text-sm font-semibold text-gray-600">Hooks used</th>
                <th className="py-3 px-4 text-sm font-semibold text-gray-600">Billable hooks</th>
                <th className="py-3 px-4 text-sm font-semibold text-gray-600">Total cost</th>
                {canListAgencies && <th className="py-3 px-4 text-sm font-semibold text-gray-600 w-20">Edit</th>}
              </tr>
            </thead>
            <tbody>
              {hookSummaryRows.length > 0 ? (
                hookSummaryRows.map((r) => (
                  <tr key={r.agency_id} className="hover:bg-gray-50/50 transition-colors border-b border-gray-100 last:border-b-0">
                    {!agencyScoped && <td className="py-3 px-4 text-sm text-gray-700 font-medium">{r.agency_name}</td>}
                    <td className="py-3 px-4 text-sm text-gray-600">{r.max_free_hooks ?? "—"}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{r.cost_per_hook ?? "—"}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{r.hooks_used}</td>
                    <td className="py-3 px-4 text-sm text-gray-600">{r.billable_hooks}</td>
                    <td className="py-3 px-4 text-sm font-medium text-gray-800">{r.total_cost}</td>
                    {canListAgencies && (
                      <td className="py-3 px-4 text-sm">
                        <button
                          type="button"
                          onClick={() => setHookPricingModal({ open: true, agency: { id: r.agency_id, name: r.agency_name, cost_per_hook: r.cost_per_hook, max_free_hooks: r.max_free_hooks } })}
                          className="text-primaryPurple hover:underline font-medium flex items-center gap-1"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Edit
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={canListAgencies ? (agencyScoped ? 6 : 7) : (agencyScoped ? 5 : 6)} className="py-8 text-center text-gray-400 italic text-sm">
                    {hookSummaryLoading ? "Loading…" : "No data for the selected range."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      <div className="flex flex-col min-h-[calc(100vh-200px)] rounded-xl border border-[#e5e7eb] bg-white overflow-hidden">
        <div className="p-4 flex flex-wrap gap-3 items-center border-b border-gray-200">
          {!agencyScoped && canListAgencies && (
            <Select value={agencyFilter || "all"} onValueChange={(v) => setAgencyFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {agencies.map((a, i) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {agencyDisplayName(a, i)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Select value={adTypeFilter || "all"} onValueChange={(v) => setAdTypeFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Ad type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ad types</SelectItem>
              {adtypes.map((at) => (
                <SelectItem key={at.id} value={String(at.id)}>
                  {at.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-x-auto flex-1 min-h-0">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">Ad type</th>
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">Price per unit</th>
                <th className="py-4 px-4 text-sm font-semibold text-gray-600">Currency</th>
                {canManageRateCard && (
                  <th className="py-4 px-4 text-sm font-semibold text-gray-600 w-28">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {rateCards.length > 0 ? (
                rateCards.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-4 px-4 text-sm text-gray-600">{r.adtype_name || "—"}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">{r.price_per_unit ?? "—"}</td>
                    <td className="py-4 px-4 text-sm text-gray-600">{r.currency || "—"}</td>
                    {canManageRateCard && (
                      <td className="py-4 px-4 text-sm">
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setModal({ open: true, editData: r })}
                            className="text-primaryPurple hover:underline font-medium"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(r)}
                            disabled={isDeleting}
                            className="text-rose-600 hover:underline font-medium disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={canManageRateCard ? 4 : 3} className="py-12 text-center text-gray-400 italic">
                    No rate cards found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30 flex-shrink-0">
          <span className="text-sm text-muted-foreground">
            Showing {rateCards.length ? page * LIMIT + 1 : 0}–{Math.min(page * LIMIT + rateCards.length, totalCount)} of {totalCount}
          </span>
          <div className="flex gap-1.5">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="h-8">
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page * LIMIT + rateCards.length >= totalCount}
              onClick={() => setPage((p) => p + 1)}
              className="h-8"
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {addCombinedOpen && (
        <AddAdTypeAndRateCardModal open={addCombinedOpen} onClose={() => setAddCombinedOpen(false)} agencyScoped={agencyScoped} scopedAgencyId={scopedAgencyId} />
      )}
      {modal.open && (
        <RateCardModal
          open={modal.open}
          onClose={() => setModal({ open: false, editData: null })}
          editData={modal.editData}
          agencyScoped={agencyScoped}
          scopedAgencyId={scopedAgencyId}
        />
      )}
      {hookPricingModal.open && (
        <HookPricingModal
          open={hookPricingModal.open}
          onClose={() => setHookPricingModal({ open: false, agency: null })}
          agency={hookPricingModal.agency}
        />
      )}
    </section>
  );
};

export default AdRateCardSection;
